import { View } from 'react-native';
import { Image } from 'expo-image';
import { observer } from 'mobx-react-lite';
import { TestIDs } from 'src/constants/testIDs.constants';
import { modalTestID } from 'src/helpers/testIDs.helper';
import { brand, neutrals } from 'src/theme/colors';
import Button from '@photosi/rn-components/buttons/button/Button';
import { FocusGroups } from '@photosi/rn-components/focus-provider/FocusProvider.constants';
import TextLato from '@photosi/rn-components/text/text-lato/TextLato';
import { styles } from './Tile.style';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';
import { Icon as IconColor } from 'src/theme-v2/icon';
import { Icon } from '@photosi/rn-components/icon/Icon';
import { ICON_NAMES } from '@photosi/rn-components/icon/icon.helper';
import { Stroke } from 'src/theme-v2/stroke';

export interface TileProps {
  code: string;
  title: string;
  price?: string;
  fullPrice?: string;
  description?: string;
  previewImageUrl: string;
  index: number;
  isActive?: boolean;
  isDisabled?: boolean;
  isSelected?: boolean;
  onPress?: () => void;
  width: number;
  borderColor?: string;
  backgroundColor?: string;
}

export const Tile = observer(
  ({
    code,
    title,
    price,
    fullPrice,
    description,
    previewImageUrl,
    index,
    isActive,
    isDisabled,
    isSelected,
    onPress,
    width,
    borderColor = `${neutrals.white}00`,
    backgroundColor = neutrals.white,
  }: TileProps) => {
    const { isDesktop } = useResponsiveScreen();

    return (
      <Button
        onPress={async () => onPress && onPress()}
        testID={modalTestID(TestIDs.CONFIGURATION_STEPS, index.toString())}
        focusId={modalTestID(TestIDs.CONFIGURATION_STEPS, index.toString())}
        focusGroup={FocusGroups.MODAL}
        focusOrder={index}
        key={`tile_${code}`}
        focusable={isActive}
        disabled={isDisabled}
        accessibilityLabel={title}
        accessibilityRole="radio"
        accessibilityState={{ checked: isSelected, disabled: isDisabled }}
      >
        {({ hovered }) => {
          const borderSize = isSelected ? 2 : hovered ? 1 : 0;
          return (
            <View style={[styles.card, { width, backgroundColor }, isDisabled && styles.imageDisabled]}>
              <View style={{ width: '100%', position: 'relative' }}>
                {borderSize > 0 && (
                  <View
                    pointerEvents="none"
                    style={[
                      styles.outerBorder,
                      {
                        top: 2 - borderSize,
                        left: 2 - borderSize,
                        right: 2 - borderSize,
                        bottom: 2 - borderSize,
                        borderWidth: borderSize,
                        borderColor: Stroke.StrokeColor.StrokeColorActive,
                      },
                    ]}
                  />
                )}
                <View style={styles.innerContainer}>
                  <Image
                    style={[
                      styles.image,
                      {
                        width: '100%',
                        aspectRatio: isDesktop ? 1.67 : 1.15,
                      },
                    ]}
                    source={{ uri: previewImageUrl }}
                    contentFit="fill"
                    accessible={false}
                  />
                  {isSelected && (
                    <View style={styles.radioButton}>
                      <Icon
                        name={ICON_NAMES.CheckCircle}
                        color={IconColor.IconColorInteractive}
                        width={20}
                        height={20}
                      />
                    </View>
                  )}
                </View>
              </View>
              <View style={styles.bottomContainer}>
                <View style={styles.textWrapperCenter}>
                  <TextLato numberOfLines={1} style={styles.textTitle}>
                    {title}
                  </TextLato>
                </View>
                <View style={isDesktop ? styles.textContainer : styles.textContainerColumn}>
                  {!!fullPrice?.length && (
                    <View style={styles.textWrapperCenter}>
                      <TextLato numberOfLines={1} style={!!price?.length ? styles.priceDiscount : styles.price}>
                        {fullPrice}
                      </TextLato>
                    </View>
                  )}
                  {!!price?.length && (
                    <View style={styles.textWrapperCenter}>
                      <TextLato numberOfLines={1} style={styles.priceColored}>
                        {price}
                      </TextLato>
                    </View>
                  )}
                </View>
                {!!description && (
                  <View style={styles.textWrapperCenter}>
                    <TextLato numberOfLines={2} style={styles.description}>
                      {description}
                    </TextLato>
                  </View>
                )}
              </View>
            </View>
          );
        }}
      </Button>
    );
  },
);
